/* eslint-disable react/react-in-jsx-scope */
import * as React from 'react'
import { Page, Path, StyleSheet, Svg, Text, View } from '@react-pdf/renderer'
import dayjs from 'dayjs'
import { Completion } from '@src/generatedClient/generated/taskreportsApi'

// Define styles for the PDF
const styles = StyleSheet.create({
  page: {
    padding: 30,
    backgroundColor: '#FFFFFF',
  },
  header: {
    marginBottom: 20,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  logo: {
    width: 50,
    height: 50,
  },
  reportTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1E293B',
  },
  reportDate: {
    fontSize: 14,
    color: '#64748B',
    marginTop: 5,
  },
  divider: {
    height: 2,
    backgroundColor: '#E2E8F0',
    marginVertical: 5,
  },
  table: {
    display: 'flex',
    width: 'auto',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F8FAFC',
    padding: 8,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderLeftColor: '#E2E8F0',
    borderRightColor: '#E2E8F0',
    borderTopColor: '#E2E8F0',
    borderTopWidth: 1,
  },
  tableRow: {
    flexDirection: 'row',
    borderTopColor: '#E2E8F0',
    borderTopWidth: 0.5,
    padding: 8,
    minHeight: 40,
    alignItems: 'center',
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderLeftColor: '#E2E8F0',
    borderRightColor: '#E2E8F0',
    borderBottomColor: '#E2E8F0',
    borderBottomWidth: 0.5,
  },
  tableRowEven: {
    backgroundColor: '#F8FAFC',
  },
  tableRowLast: {
    borderBottomWidth: 1,
  },
  tableHeaderCell: {
    fontWeight: 'bold',
    color: '#475569',
    fontSize: 12,
  },
  tableCell: {
    fontSize: 11,
    color: '#334155',
  },
  titleColumn: {
    width: '40%',
    paddingRight: 8,
  },
  dateColumn: {
    width: '30%',
    paddingRight: 8,
  },
  statusColumn: {
    width: '30%',
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusIndicator: {
    width: 16,
    height: 16,
    borderRadius: 8,
    marginRight: 5,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  statusComplete: {
    backgroundColor: '#10B981',
  },
  statusIncomplete: {
    backgroundColor: '#EF4444',
  },
  statusText: {
    fontSize: 11,
  },
  statusTextComplete: {
    color: '#10B981',
  },
  statusTextIncomplete: {
    color: '#EF4444',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 10,
    color: '#94A3B8',
  },
  summary: {
    marginTop: 20,
    padding: 15,
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
  },
  summaryTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#334155',
    marginBottom: 8,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 5,
  },
  summaryLabel: {
    fontSize: 11,
    color: '#64748B',
  },
  summaryValue: {
    fontSize: 11,
    fontWeight: 'semibold',
    color: '#334155',
  },
  noActivityMessage: {
    fontSize: 14,
    textAlign: 'center',
    marginTop: 40,
    fontStyle: 'italic',
  },
  icon: {
    width: 12,
    height: 12,
  },
})

// Status indicator SVG icons
function CheckIcon() {
  return (
    <Svg viewBox="0 0 24 24" style={styles.icon}>
      <Path
        d="M20 6L9 17l-5-5"
        stroke="#ffffff"
        strokeWidth={2}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  )
}

function XIcon() {
  return (
    <Svg viewBox="0 0 24 24" style={styles.icon}>
      <Path
        d="M6 6l12 12M6 18L18 6"
        stroke="#ffffff"
        strokeWidth={2}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  )
}

const formatDateTime = (date: string): string =>
  dayjs(date).format('MMM D, YYYY h:mm A')

export function CompletionPdf(props: {
  completions: Completion[]
}) {
  const { completions } = props

  const hasNoCompletions = completions.length === 0

  return (
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <View>
            <Text style={styles.reportTitle}>Completion Report</Text>
          </View>
        </View>
      </View>

      {hasNoCompletions ? (
        <Text style={styles.noActivityMessage}>
          No task completions found.
        </Text>
      ) : (
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, styles.titleColumn]}>
              Title
            </Text>
            <Text style={[styles.tableHeaderCell, styles.dateColumn]}>
              Due Date
            </Text>
            <Text style={[styles.tableHeaderCell, styles.dateColumn]}>
              Completed At
            </Text>
          </View>

          {completions.map((completion, index) => (
            <View
              key={completion.id}
              wrap={false}
              style={[
                styles.tableRow,
                index % 2 === 1 && styles.tableRowEven,
                index === completions.length - 1 && styles.tableRowLast,
              ]}
            >
              <Text style={[styles.tableCell, styles.titleColumn]}>
                {completion.title}
              </Text>
              <Text style={[styles.tableCell, styles.dateColumn]}>
                {formatDateTime(completion.dueDate)}
              </Text>
              <View style={styles.statusColumn}>
                <View
                  style={[
                    styles.statusIndicator,
                    completion.isComplete
                      ? styles.statusComplete
                      : styles.statusIncomplete,
                  ]}
                >
                  {completion.isComplete ? <CheckIcon /> : <XIcon />}
                </View>
                <Text
                  style={[
                    styles.statusText,
                    completion.isComplete
                      ? styles.statusTextComplete
                      : styles.statusTextIncomplete,
                  ]}
                >
                  {completion.isComplete
                    ? formatDateTime(completion.completedAtTime)
                    : 'Not completed'}
                </Text>
              </View>
            </View>
          ))}
        </View>
      )}
    </Page>
  )
}
